import React, { useState } from 'react';
import P from 'prop-types';
import { useQuery } from '@nerdwallet/apollo';
import { Box } from '@nerdwallet/currency';
import Image from '@nerdwallet/react-image';

import PlayButtonDefaultIcon from '../assets/play-button-default-image';
import PlayButtonDefaultWhiteIcon from '../assets/play-button-default-white';

import VideoJsonLd, { QUERY } from '../videoJsonLd';
import { boolCheckboxSpec, group, stringInputSpec } from '../editing-configs';
import './youtube-player.less';
import UniversalContainer, {
  universalContainerPropTypes,
} from '../../components/universal-container/universal-container';

const defaultDimensions = {
  height: '232px',
  width: '500px',
};

const defaultPlayerPlaceholderImageUrl =
  'https://www.nerdwallet.com/cdn/img/lpc/no-video-image.jpg';

const renderDefaultPlayButton = (defaultPlayButtonIcon) => {
  const type = {
    default: <PlayButtonDefaultIcon />,
    white: <PlayButtonDefaultWhiteIcon />,
  };
  return type[defaultPlayButtonIcon];
};

const renderPlayerContent = ({
  src,
  handleLoadPlayer,
  youtubeMaxHeight,
  youtubeMaxWidth,
  showPlayer,
  youtubeVideo,
  data,
  youtubePlayButtonImageUrl,
  defaultPlayButtonIcon,
  imagePlaceholderCoverSize,
  youtubePlaceholderImageUrl,
}) => (
  <Box
    id="you-tube-player-container"
    onClick={handleLoadPlayer}
    style={{
      maxHeight: youtubeMaxHeight || defaultDimensions.height,
      width: youtubeMaxWidth || defaultDimensions.width,
    }}
    className="video-placeholder"
  >
    {showPlayer ? (
      <React.Fragment>
        <iframe
          title="video embed"
          id="youtube-iframe"
          type="text/html"
          width="100%"
          height="100%"
          src={src}
          frameBorder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
        {youtubeVideo && <VideoJsonLd {...data.youtubeVideo} />}
      </React.Fragment>
    ) : (
      <React.Fragment>
        {youtubePlayButtonImageUrl ? (
          <img
            className="custom-btn-image"
            id="default-player-btn"
            src={youtubePlayButtonImageUrl}
            alt="Play Button"
          />
        ) : (
          renderDefaultPlayButton(defaultPlayButtonIcon)
        )}
        <Image
          objectFit={imagePlaceholderCoverSize}
          src={youtubePlaceholderImageUrl || defaultPlayerPlaceholderImageUrl}
          alt="player-cover-image"
          backgroundColor="transparent"
          naturalWidth={564}
          naturalHeight={316}
          className={youtubePlaceholderImageUrl ? '' : 'gray-out'}
          lazy
        />
      </React.Fragment>
    )}
  </Box>
);

const YouTubePlayer = ({
  youtubeVideoSlug,
  youtubeMaxWidth,
  youtubeMaxHeight,
  youtubePlaceholderImageUrl,
  youtubePlayButtonImageUrl,
  defaultPlayButtonIcon,
  imagePlaceholderCoverSize,
  enableModestbranding,
  privacyModeToggle,
  controlsToggle,
  youtubeContainer,
  includeContainer,
}) => {
  const modestbranding = enableModestbranding ? '&modestbranding=1' : '';
  const privacyEnabled = 'youtube-nocookie';
  const privacyNotEnabled = 'youtube';
  const origin = '?origin=https://www.nerdwallet.com';
  const showControls = '';
  const hideControls = '&controls=0';
  const controls = controlsToggle ? hideControls : showControls;
  const youtubeDomain = privacyModeToggle ? privacyEnabled : privacyNotEnabled;
  const removeRelated = '&rel=0';
  const hiDef = '&hd=1';
  const src = `https://www.${youtubeDomain}.com/embed/${youtubeVideoSlug}${origin}${controls}${removeRelated}${hiDef}${modestbranding}`;
  const [showPlayer, setShowPlayer] = useState(false);

  const { data, loading } = useQuery(QUERY, {
    variables: {
      youtubeVideoId: youtubeVideoSlug,
    },
  });
  const youtubeVideo = data?.youtubeVideo;

  const handleLoadPlayer = () => {
    setShowPlayer(true);
  };

  if (loading) {
    return null;
  }

  if (includeContainer) {
    return (
      <UniversalContainer {...youtubeContainer}>
        {renderPlayerContent({
          src,
          data,
          defaultPlayButtonIcon,
          handleLoadPlayer,
          imagePlaceholderCoverSize,
          showPlayer,
          youtubeMaxHeight,
          youtubeMaxWidth,
          youtubePlaceholderImageUrl,
          youtubePlayButtonImageUrl,
          youtubeVideo,
        })}
      </UniversalContainer>
    );
  }

  return renderPlayerContent({
    src,
    data,
    defaultPlayButtonIcon,
    handleLoadPlayer,
    imagePlaceholderCoverSize,
    showPlayer,
    youtubeMaxHeight,
    youtubeMaxWidth,
    youtubePlaceholderImageUrl,
    youtubePlayButtonImageUrl,
    youtubeVideo,
  });
};

YouTubePlayer.propTypes = {
  youtubeVideoSlug: P.string,
  youtubeMaxWidth: P.string,
  youtubeMaxHeight: P.string,
  youtubePlayButtonImageUrl: P.string,
  youtubePlaceholderImageUrl: P.string,
  defaultPlayButtonIcon: P.oneOf(['default', 'white']),
  imagePlaceholderCoverSize: P.oneOf(['auto', 'contain', 'cover']),
  enableModestbranding: P.bool,
  privacyModeToggle: P.bool,
  controlsToggle: P.bool,
  includeContainer: P.bool,
  youtubeContainer: universalContainerPropTypes,
};

YouTubePlayer.defaultProps = {
  youtubeVideoSlug: '',
  youtubeMaxWidth: '',
  youtubeMaxHeight: '',
  youtubePlayButtonImageUrl: '',
  youtubePlaceholderImageUrl: '',
  defaultPlayButtonIcon: 'default',
  imagePlaceholderCoverSize: 'cover',
  enableModestbranding: false,
  privacyModeToggle: false,
  controlsToggle: false,
};

YouTubePlayer.getEditingConfig = (
  _componentDocument,
  fieldName,
  includeContainer = false
) => {
  const getInputName = (name, topLevel = false) => {
    if (topLevel) {
      return `${fieldName?.split('.').at(-1)}.${name}`;
    }

    if (fieldName) {
      return `${fieldName}.${name}`;
    }
    return name;
  };

  return [
    stringInputSpec(getInputName('youtubeVideoSlug', true), 'Video Slug', {
      help: 'The Video Slug is the string of text/numerals that immediately follows the https://youtu.be/ portion of the Share URL generated by YouTube.',
    }),
    stringInputSpec(
      getInputName('youtubePlaceholderImageUrl', true),
      'Video image placeholder',
      {
        help: 'Video preview image output upon page load',
      }
    ),
    stringInputSpec(
      getInputName('youtubePlayButtonImageUrl', true),
      'Play button image',
      {
        help: 'Image output as the play button',
      }
    ),
    group('Video Details', [
      stringInputSpec(getInputName('youtubeMaxHeight'), 'Player max height', {
        help: 'The player frame max height in pixels or percentage. Default value is 232px',
      }),
      stringInputSpec(getInputName('youtubeMaxWidth'), 'Player max width', {
        help: 'The player frame max width in pixels or percentage. Default value is 500px',
      }),
      boolCheckboxSpec(
        getInputName('enableModestbranding'),
        'Enable Modestbranding?',
        {
          help: 'If checked the player will not show a YouTube logo',
        }
      ),
      boolCheckboxSpec(
        getInputName('privacyModeToggle'),
        'Enable Privacy Mode?'
      ),
      boolCheckboxSpec(getInputName('controlsToggle'), 'Hide Controls?'),
      ...(includeContainer
        ? [
            ...UniversalContainer.getEditingConfig(
              _componentDocument,
              'Container',
              ['padding', 'margin'],
              getInputName('youtubeContainer'),
              { disableGroup: true }
            ),
          ]
        : []),
    ]),
  ];
};

export default YouTubePlayer;
